import { useEffect, useRef, useState } from "react";
import { useWizardStore } from "../../store/wizardStore";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { Button } from "../ui/Button";
import { CardHeader, CardTitle, CardContent } from "../ui/Card";
import { RECIPIENT_TYPE_LABELS, YEAR_GROUP_LABELS, type RecipientType, type YearGroup } from "../../lib/types";
import { api } from "../../lib/api";

export default function Step4GiftBuilder() {
  const { gifts, pickupLocation, addGift, removeGift, updateGift, nextStep, previousStep, setStep, setWantsSanta, orderId } =
    useWizardStore();
  const [recipientType, setRecipientType] =
    useState<RecipientType>("adult_male");
  const [recipientName, setRecipientName] = useState("");
  const [fromName, setFromName] = useState("");
  const [yearGroup, setYearGroup] = useState<YearGroup>("reception");
  const [errors, setErrors] = useState<{
    recipientName?: string;
    fromName?: string;
    yearGroup?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [basketError, setBasketError] = useState<string | null>(null);
  const recipientSelectRef = useRef<HTMLSelectElement>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // Where to go after the foodbank prompt; null while building gifts
  const [donationNext, setDonationNext] = useState<"santa" | "payment" | null>(null);
  const hasDonation = gifts.some((g) => g.recipientType === "foodbank");

  const recipientOptions = Object.entries(RECIPIENT_TYPE_LABELS).map(
    ([value, label]) => ({
      value,
      label,
    })
  );

  const yearGroupOptions = Object.entries(YEAR_GROUP_LABELS).map(
    ([value, label]) => ({
      value,
      label,
    })
  );

  const canAddMore = gifts.length < 10;
  const isFoodbankDonation = recipientType === 'foodbank';

  const handleAddGift = async () => {
    const newErrors: { recipientName?: string; fromName?: string; yearGroup?: string } = {};

    // Only validate these fields if NOT a foodbank donation
    if (!isFoodbankDonation) {
      if (!recipientName.trim()) {
        newErrors.recipientName = "Please enter a name for the gift";
      }

      if (!fromName.trim()) {
        newErrors.fromName = "Please enter who this gift is from";
      }

      if (!yearGroup) {
        newErrors.yearGroup = "Please select a year group";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0 && orderId && pickupLocation) {
      setIsLoading(true);
      try {
        // If editing, delete the old gift first
        if (editingIndex !== null) {
          const oldGift = gifts[editingIndex];
          if (oldGift.id) {
            await api.deleteGift(orderId, oldGift.id);
          }
        }

        // Save gift to database (creates new gift)
        const savedGift = await api.addGift(orderId, {
          pickupLocation,
          recipientType,
          recipientName: isFoodbankDonation ? 'Foodbank' : recipientName,
          fromName: isFoodbankDonation ? 'Anonymous' : fromName,
          yearGroup: isFoodbankDonation ? 'reception' : yearGroup,
        });

        if (editingIndex !== null) {
          // Update existing gift in wizard state
          updateGift(editingIndex, {
            id: savedGift.id,
            pickupLocation,
            recipientType,
            recipientName: isFoodbankDonation ? 'Foodbank' : recipientName,
            fromName: isFoodbankDonation ? 'Anonymous' : fromName,
            yearGroup: isFoodbankDonation ? 'reception' : yearGroup,
            price: savedGift.price,
          });
          setEditingIndex(null);
        } else {
          // Add new gift
          addGift({
            id: savedGift.id,
            pickupLocation,
            recipientType,
            recipientName: isFoodbankDonation ? 'Foodbank' : recipientName,
            fromName: isFoodbankDonation ? 'Anonymous' : fromName,
            yearGroup: isFoodbankDonation ? 'reception' : yearGroup,
            price: savedGift.price,
          });
        }

        // Reset form
        setRecipientName("");
        setFromName("");
        setRecipientType("adult_male");
        setYearGroup("reception");
      } catch (error) {
        console.error("Failed to add gift:", error);
        setErrors({ recipientName: "Failed to save gift. Please try again." });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteGift = async (index: number) => {
    const gift = gifts[index];
    if (!gift.id || !orderId) return;

    setIsLoading(true);
    setBasketError(null);
    try {
      await api.deleteGift(orderId, gift.id);
      removeGift(index);
      // The focused delete button is gone; land on the step title rather than the page body
      document.querySelector<HTMLElement>("main h2")?.focus();
    } catch (error) {
      console.error("Failed to delete gift:", error);
      setBasketError("Failed to remove that gift. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditGift = (index: number) => {
    const gift = gifts[index];
    setRecipientType(gift.recipientType);
    setRecipientName(gift.recipientName);
    setFromName(gift.fromName);
    setYearGroup(gift.yearGroup);
    setEditingIndex(index);

    // Take the parent straight to the form (it sits below the basket)
    requestAnimationFrame(() => recipientSelectRef.current?.focus());
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setRecipientName("");
    setFromName("");
    setRecipientType("adult_male");
    setYearGroup("reception");
  };

  const goToSanta = () => nextStep();

  const goToPayment = () => {
    // Mark that user doesn't want Santa visit
    setWantsSanta(false);
    // Jump to tokens page (step 7) - good upselling opportunity!
    setStep(7);
  };

  const handleNext = () => {
    if (gifts.length === 0) {
      setErrors({ recipientName: "Please add at least one gift" });
      return;
    }
    if (hasDonation) goToSanta();
    else setDonationNext("santa");
  };

  const handleSkipToPayment = () => {
    if (gifts.length === 0) {
      setErrors({ recipientName: "Please add at least one gift" });
      return;
    }
    if (hasDonation) goToPayment();
    else setDonationNext("payment");
  };

  if (donationNext) {
    return (
      <FoodbankPrompt
        onDone={donationNext === "santa" ? goToSanta : goToPayment}
        onBack={() => setDonationNext(null)}
      />
    );
  }

  return (
    <>
      <CardHeader>
        <CardTitle>
          Build Your Gift {gifts.length > 0 && `(${gifts.length}/10)`}
        </CardTitle>
        <p className="text-gray-600 mt-2">
          Tell us about the gift you'd like to purchase.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {basketError && (
            <div role="alert" className="bg-primary-50 border border-primary-300 p-4 rounded-lg">
              <p className="text-primary-800">{basketError}</p>
            </div>
          )}

          {/* Gifts added summary - Basket Style */}
          {gifts.length > 0 && (
            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-secondary text-secondary-foreground px-4 py-3">
                <h3 className="font-semibold flex items-center">
                  <svg aria-hidden="true" className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Your Basket ({gifts.length} {gifts.length === 1 ? 'item' : 'items'})
                </h3>
              </div>
              <div className="divide-y divide-gray-200">
                {gifts.map((gift, index) => (
                  <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {gift.recipientType === 'foodbank'
                            ? 'Foodbank Donation'
                            : `Gift for ${gift.recipientName}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          {RECIPIENT_TYPE_LABELS[gift.recipientType]}
                        </p>
                        {gift.recipientType !== 'foodbank' && (
                          <p className="text-xs text-gray-500 mt-1">
                            From: {gift.fromName} ({YEAR_GROUP_LABELS[gift.yearGroup]})
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-secondary-foreground">
                          £{gift.price.toFixed(2)}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditGift(index)}
                          disabled={isLoading}
                          className="p-3 text-blue-700 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                          aria-label={`Edit gift for ${gift.recipientType === 'foodbank' ? 'foodbank' : gift.recipientName}`}
                        >
                          <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteGift(index)}
                          disabled={isLoading}
                          className="p-3 text-primary-800 hover:bg-primary-50 rounded transition-colors disabled:opacity-50"
                          aria-label={`Delete gift for ${gift.recipientType === 'foodbank' ? 'foodbank' : gift.recipientName}`}
                        >
                          <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 px-4 py-3 border-t-2 border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Subtotal:</span>
                  <span className="font-bold text-xl text-secondary-foreground">
                    £{gifts.reduce((sum, gift) => sum + gift.price, 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {(canAddMore || editingIndex !== null) && (
            <>
              {editingIndex !== null && (
                <div role="status" className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">
                    Editing gift {editingIndex + 1}
                  </p>
                </div>
              )}
              <Select
                ref={recipientSelectRef}
                label="Who is this gift for?"
                options={recipientOptions}
                value={recipientType}
                onChange={(e) =>
                  setRecipientType(e.target.value as RecipientType)
                }
              />

              {!isFoodbankDonation && (
                <>
                  <Input
                    label="Gift recipient name (e.g., 'Daddy', 'Mummy')"
                    type="text"
                    placeholder="Daddy"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    error={errors.recipientName}
                  />

                  <Input
                    label="Who is this gift from? (e.g., 'Sammy')"
                    type="text"
                    placeholder="Sammy"
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    error={errors.fromName}
                  />

                  <Select
                    label="Year Group (of the child buying this gift)"
                    options={yearGroupOptions}
                    value={yearGroup}
                    onChange={(e) => setYearGroup(e.target.value as YearGroup)}
                    error={errors.yearGroup}
                  />
                </>
              )}

              {isFoodbankDonation && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Thank you for choosing to donate to the foodbank. This donation will help those in need in our community.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Button
                  onClick={handleAddGift}
                  fullWidth
                  variant="primary"
                  disabled={isLoading}
                >
                  {isLoading
                    ? (editingIndex !== null ? "Updating..." : "Adding...")
                    : editingIndex !== null
                      ? "Update Gift"
                      : isFoodbankDonation ? "Add Donation" : "Add This Gift"}
                </Button>
                {editingIndex !== null && (
                  <Button
                    onClick={handleCancelEdit}
                    fullWidth
                    variant="ghost"
                    disabled={isLoading}
                  >
                    Cancel Edit
                  </Button>
                )}
              </div>
            </>
          )}

          {!canAddMore && (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg text-center">
              <p className="text-amber-800 font-medium">
                Maximum of 10 gifts reached
              </p>
            </div>
          )}

          <div className="pt-4 space-y-2">
            {gifts.length > 0 && (
              <>
                <Button onClick={handleNext} fullWidth size="lg">
                  Continue to Santa Booking
                </Button>
                <Button onClick={handleSkipToPayment} fullWidth size="lg" variant="outline">
                  Just Go to Payment
                </Button>
              </>
            )}
            <Button onClick={previousStep} fullWidth variant="ghost">
              Back
            </Button>
          </div>
        </div>
      </CardContent>
    </>
  );
}

const MAX_DONATIONS = 10;

// Shown once when leaving the gift builder without a foodbank donation in the basket.
function FoodbankPrompt({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const { orderId, pickupLocation, addGift } = useWizardStore();
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    titleRef.current?.focus();
    api.getPricing()
      .then((pricing) => setPrice(pricing.gift_foodbank ?? null))
      .catch(() => setError("Couldn't load the donation price. Please try again."));
  }, []);

  const handleAdd = async () => {
    if (!orderId || !pickupLocation) return;
    setIsSaving(true);
    setError(null);
    let added = 0;
    try {
      // ponytail: one gift row per donation, same as adding them in the builder
      for (let i = 0; i < quantity; i++) {
        const saved = await api.addGift(orderId, {
          pickupLocation,
          recipientType: "foodbank",
          recipientName: "Foodbank",
          fromName: "Anonymous",
          yearGroup: "reception",
        });
        addGift({
          id: saved.id,
          pickupLocation,
          recipientType: "foodbank",
          recipientName: "Foodbank",
          fromName: "Anonymous",
          yearGroup: "reception",
          price: saved.price,
        });
        added++;
      }
      onDone();
    } catch (err) {
      console.error("Failed to add donation:", err);
      // Only retry what didn't save, so a retry can't double-charge
      setQuantity(Math.max(quantity - added, 1));
      setError(added > 0
        ? `${added} of ${quantity} donations were added. Please try again to add the rest.`
        : "Failed to add your donation. Please try again.");
      setIsSaving(false);
    }
  };

  return (
    <>
      <CardHeader>
        <CardTitle ref={titleRef}>Add a foodbank donation?</CardTitle>
        <p className="text-gray-600 mt-2">
          All the money from foodbank donations is passed on to{" "}
          <a
            href="https://www.placesforpeople.co.uk/about-us/community/community-projects/st-aidans-church-foodbank-leeds/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-gray-900 underline underline-offset-2 hover:text-secondary-foreground"
          >
            St Aidan's Church Foodbank, Leeds
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          .
          {price !== null && ` Each donation is £${price.toFixed(2)}.`}
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {error && (
            <div role="alert" className="bg-primary-50 border border-primary-300 p-4 rounded-lg">
              <p className="text-primary-800">{error}</p>
            </div>
          )}

          <div className="bg-white border-2 border-gray-200 rounded-lg p-6 text-center">
            <p id="donation-count-label" className="text-sm text-gray-600 font-medium mb-4">
              How many donations would you like to add?
            </p>
            <div className="flex items-center justify-center gap-4" role="group" aria-labelledby="donation-count-label">
              <button
                onClick={() => setQuantity((q) => q - 1)}
                disabled={quantity <= 1 || isSaving}
                className="w-12 h-12 rounded-full border-2 border-accent-800 bg-accent-50 text-accent-900 hover:bg-accent flex items-center justify-center transition-all active:scale-95 motion-reduce:active:scale-100 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Fewer donations"
              >
                <svg aria-hidden="true" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                </svg>
              </button>
              <p className="min-w-[80px] text-5xl font-bold text-gray-900" aria-live="polite">{quantity}</p>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                disabled={quantity >= MAX_DONATIONS || isSaving}
                className="w-12 h-12 rounded-full border-2 border-accent-800 bg-accent-50 text-accent-900 hover:bg-accent flex items-center justify-center transition-all active:scale-95 motion-reduce:active:scale-100 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="More donations"
              >
                <svg aria-hidden="true" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Button onClick={handleAdd} fullWidth size="lg" disabled={isSaving || price === null}>
              {isSaving
                ? "Adding..."
                : `Add ${quantity} ${quantity === 1 ? "donation" : "donations"}${price !== null ? ` (£${(price * quantity).toFixed(2)})` : ""}`}
            </Button>
            <Button onClick={onDone} fullWidth size="lg" variant="outline" disabled={isSaving}>
              No thanks
            </Button>
            <Button onClick={onBack} fullWidth variant="ghost" disabled={isSaving}>
              Back to gifts
            </Button>
          </div>
        </div>
      </CardContent>
    </>
  );
}
